import {
	ColorPaletteEnum,
	DataTableLocal,
	DataColumn,
	DataEnum,
	FlowConnector,
	Localization,
	PaletteFactory,
	RepresentationEnum,
	ThemeElementEnum,
	Util
} from "cartovista";

	var KEY_FLOW_ORIGIN = "flowLayer.origin";
	var KEY_FLOW_DESTINATION = "flowLayer.destination";
	var KEY_REFERENCE_LAYER_FLOW_DATA_TABLE_NAME = "flowLayer.referenceLayerFlowDataTableName";
	var SHOW_MULTI_FLOW_PIE_CHART = false;
	var REFERENCE_LAYER_FLOW_DATA_TABLE_ID = "Flow";


	/**
	 * Connector used to get data from a single url to feed the flow controller.
	 * This can load data from a server or a flat file.
	 *
	 * @namespace .
	 * @extends cartovista.viewer.controllers.FlowConnector
	 *
	 * @class JSONFlowConnector
	 * @constructor
	 *
	 * @param {cartovista.core.layers.vector.Interactivelayer} referenceLayer Layer the feature id comes from.
	 * @param {String} [arrayField=null] Field name to use on the json data received to get the data array. If json data is already an array, set this to null.
	 * @param {String} [originFeatureIdField=null] Field name to use for origin feature id.
	 * @param {String} [originFeatureNameField=null] Field name to use for origin feature name. If not set, name can comes from the reference layer if featureNameDataColumn is set on the layer.
	 * @param {String} [destinationFeatureIdField=null] Field name to use for destination feature id.
	 * @param {String} [destinationFeatureNameField=null] Field name to use for destination feature name. If not set, name can comes from the reference layer if featureNameDataColumn is set on the layer.
	 * @param {String} [featureValueField='featureValue'] Field name to use for the flow value.
	 * @param {String} [flowDataFilePath='flow-data/flow-data.json'] Flow data path and file name relative to the appDir parent folder.
	 */
	var JSONFlowConnector = FlowConnector.extend({
		initialize: function() {
			FlowConnector.prototype.initialize.call(this);

			this._referenceLayer = null;
			this._arrayField = null;
			this._originFeatureIdField = null;
			this._originFeatureNameField = null;
			this._destinationFeatureIdField = null;
			this._destinationFeatureNameField = null;
			this._featureValueField = null;

			this.flowDataFilePath = null;

			this._dataArray = null;
			this._dataLoaded = false;
			this.totalValues = {};
			this.dataTableReferenceLayer = null;

			this._map = window.cartovista.externalInterface.map;

		}
	});

	/**
	 * Returns the string descriptor of the object.
	 *
	 * @method toString
	 *
	 * @return {String}
	 */
	JSONFlowConnector.prototype.toString = function() {
		return "JSONFlowConnector";
	};

	/**
	 * The layer used for the flow theme.
	 * 
	 * @property referenceLayer
	 * @type {cartovista.core.layers.vector.Interactivelayer}
	 * @default defaultvalue
	 */
	Object.defineProperty(JSONFlowConnector.prototype, "referenceLayer", {
		get: function() {
			return this._referenceLayer;
		},
		set: function(value) {
			if (this._referenceLayer !== value) {
				this.cleanDataTable();
				if (this.currentActiveTheme) {
					this.currentActiveTheme.unapply();
					this.currentActiveTheme = null;
				}
				if (this.styleTheme) {
					this._defaultThemeSet.removeTheme(this.styleTheme);
					this.styleTheme = null;
				}
				this._referenceLayer = value;
				this.buildDataTable();
			}
		}
	});

	Object.defineProperty(JSONFlowConnector.prototype, "flowDataFilePath", {
		get: function() {
			return this._flowDataFilePath;
		},
		set: function(value) {
			if (this._flowDataFilePath !== value) {
				this._flowDataFilePath = value;
				this._dataLoaded = false;
				this.totalValues = {};
			}
		}
	});

	Object.defineProperty(JSONFlowConnector.prototype, "arrayField", {
		get: function() {
			return this._arrayField;
		},
		set: function(value) {
			if (this._arrayField !== value) {
				this._arrayField = value;
				this._dataLoaded = false;
				this.totalValues = {};
			}
		}
	});

	Object.defineProperty(JSONFlowConnector.prototype, "originFeatureIdField", {
		get: function() {
			return this._originFeatureIdField;
		},
		set: function(value) {
			if (this._originFeatureIdField !== value) {
				this._originFeatureIdField = value;
				this._dataLoaded = false;
				this.totalValues = {};
			}
		}
	});

	Object.defineProperty(JSONFlowConnector.prototype, "originFeatureNameField", {
		get: function() {
			return this._originFeatureNameField;
		},
		set: function(value) {
			if (this._originFeatureNameField !== value) {
				this._originFeatureNameField = value;
				this._dataLoaded = false;
				this.totalValues = {};
			}
		}
	});

	Object.defineProperty(JSONFlowConnector.prototype, "destinationFeatureIdField", {
		get: function() {
			return this._destinationFeatureIdField;
		},
		set: function(value) {
			if (this._destinationFeatureIdField !== value) {
				this._destinationFeatureIdField = value;
				this._dataLoaded = false;
				this.totalValues = {};
			}
		}
	});

	Object.defineProperty(JSONFlowConnector.prototype, "destinationFeatureNameField", {
		get: function() {
			return this._destinationFeatureNameField;
		},
		set: function(value) {
			if (this._destinationFeatureNameField !== value) {
				this._destinationFeatureNameField = value;
				this._dataLoaded = false;
				this.totalValues = {};
			}
		}
	});

	Object.defineProperty(JSONFlowConnector.prototype, "featureValueField", {
		get: function() {
			return this._featureValueField;
		},
		set: function(value) {
			if (this._featureValueField !== value) {
				this._featureValueField = value;
				this._dataLoaded = false;
				this.totalValues = {};
			}
		}
	});

	/**
	 * Return a unique key based on the feature id and flow type.
	 * 
	 * @method getFlowKey
	 * @param {String} featureId Feature if to get the key for.
	 * @param {String} flowType Flow type to get the key for.
	 * 
	 * @return {String} A key using the function arguments.
	 */
	JSONFlowConnector.prototype.getFlowKey = function(featureId, flowType) {
		featureId = featureId.replace(/-|\s/g, "_");
		return featureId + "_" + flowType;
	};

	/**
	 * Make sure the data is loaded and return the flow data related to the feature id and flow type given in parameter.
	 * This function is called by FlowConnector when an item is clicked in the map with a flow tool.
	 * 
	 * @method getFlowData
	 * @param {String} featureId Feature id to get the data for.
	 * @param {String} flowType Type of flow feature. Can be "origin" or "destination".
	 * @param {Function} callbackFunction Function to call to pass the resulted array of objects containing the following fields: originFeatureId, originFeatureName, destinationFeatureId, destinationFeatureName, featureValue.
	 */
	JSONFlowConnector.prototype.getFlowData = function(featureId, flowType, callbackFunction) {
		if (this._dataLoaded === false) {
			//Load the data for the flow layer
			var url;
			//if (flowType === "destination") {
			url = "appDir/../" + this.flowDataFilePath;
			//} else {
			//	url = ...;
			//}

			Util.loadJSON(url, this._onLoadJSONSuccess.bind(this, featureId, flowType, callbackFunction), this._onLoadJSONError);
			//Util.loadCSVCrossDomain(url, this._onLoadJSONSuccess.bind(this, featureId, flowType, callbackFunction), this._onLoadJSONError, this);
		} else {
			this._processData(featureId, flowType, callbackFunction);
		}
	};

	/**
	 * @private
	 * Success callback function for Util.loadJSON method.
	 *
	 * @method parseJSON
	 * @param {String} featureId Feature id to get the data for.
	 * @param {String} flowType Type of flow feature. Can be "origin" or "destination".
	 * @param {Function} callbackFunction Function to call to pass the resulted array of objects containing the following fields: originFeatureId, originFeatureName, destinationFeatureId, destinationFeatureName, featureValue.
	 * @param {Object} json Object in a JSON format containing loaded data.
	 */
	JSONFlowConnector.prototype._onLoadJSONSuccess = function(featureId, flowType, callbackFunction, json) {
		if (json === '{"Message":"An error has occurred."}') {
			console.warn("An error has occured with the data loading.");
		} else {
			if (typeof this._arrayField !== 'undefined' && this._arrayField !== null && this._arrayField.toString().length > 0) {
				this._dataArray = json[this._arrayField];
			} else {
				this._dataArray = json;
			}

			if (this._dataArray.length == 0) {
				var message = "";
				if (json.validation && json.validation.message) {
					message = json.validation.message;
				}
				alert("There is no data in that found: " + message);
			} else {
				this._dataLoaded = true;
			}

			this._processData(featureId, flowType, callbackFunction);
		}
	};

	/**
	 * @private
	 * Use preloaded data to return the requested ones.
	 * 
	 * @method _processData
	 * @param {String} featureId Feature id to get the data for.
	 * @param {String} flowType Type of flow feature. Can be "origin" or "destination".
	 * @param {Function} callbackFunction Function to call to pass the resulted array of objects containing the following fields: originFeatureId, originFeatureName, destinationFeatureId, destinationFeatureName, featureValue.
	 */
	JSONFlowConnector.prototype._processData = function(featureId, flowType, callbackFunction) {
		//Get referenceLayer feature names 
		if (this._referenceLayer.featureNameDataColumn) {
			var dataQuery = this._referenceLayer.createDataQuery([this._referenceLayer.featureNameDataColumn], null);
			dataQuery.execute(function(result) {
				var featuresNames = {};
				var n = result.linkingIds.length;
				for (var i = 0; i < n; i++) {
					featuresNames[result.linkingIds[i]] = result.values[this._referenceLayer.featureNameDataColumn.dataTable.id][this._referenceLayer.featureNameDataColumn.id][i];
				}

				this._returnFlowData(featureId, flowType, callbackFunction, featuresNames);
			}, this);
		} else {
			this._returnFlowData(featureId, flowType, callbackFunction, {});
		}
	};

	/**
	 * @private
	 * Methods used to throw an error for the failure of loading the JSON at a specified URL.
	 *
	 * @method _onLoadJSONError
	 * @param {XMLHttpRequest} xhr The XMLHttpRequest to go get the resource.
	 * @param {String} url The resource URL.
	 */
	JSONFlowConnector.prototype._onLoadJSONError = function(xhr, url) {
		console.warn("Failed to load flow data " + url + "from url " + window.location);
	};

	/**
	 * Use the loaded data to return the flow data related to the feature id and flow type given in parameter.
	 * 
	 * @method _returnFlowData
	 * @param {String} featureId Feature id to get the data for.
	 * @param {String} flowType Type of flow feature. Can be "origin" or "destination".
	 * @param {Function} callbackFunction Function to call to pass the resulted array of objects containing the following fields: originFeatureId, originFeatureName, destinationFeatureId, destinationFeatureName, featureValue.
	 * @param {Object} featuresNames Object containing the features names.
	 */
	JSONFlowConnector.prototype._returnFlowData = function(featureId, flowType, callbackFunction, featuresNames) {
		var totalValue = 0;
		var flowData = [];
		if (typeof featureId !== 'undefined' && featureId !== null) {

			var originDestinationName = "";
			var featureIds = [];
			var featureValues = [];
			var n = this._dataArray.length;
			var i;
			//Prepare data
			if (flowType === "origin") {
				for (i = 0; i < n; i++) {
					var featureElement = this._dataArray[i];
					if (featureElement[this._originFeatureIdField] === featureId) {
						flowData.push({
							originFeatureId: featureElement[this._originFeatureIdField],
							originFeatureName: featureElement[this._originFeatureNameField] ? featureElement[this._originFeatureNameField] : featuresNames[featureElement[this._originFeatureIdField]],
							destinationFeatureId: featureElement[this._destinationFeatureIdField],
							destinationFeatureName: featureElement[this._destinationFeatureNameField] ? featureElement[this._destinationFeatureNameField] : featuresNames[featureElement[this._destinationFeatureIdField]],
							featureValue: parseFloat(featureElement[this._featureValueField])
						});

						//Update the value of the feature in reference layer....
						featureIds.push(featureElement[this._destinationFeatureIdField]);
						featureValues.push(parseFloat(featureElement[this._featureValueField]));

						originDestinationName = featureElement[this._originFeatureNameField] ? featureElement[this._originFeatureNameField] : featuresNames[featureElement[this._originFeatureIdField]];
					}
				}
			} else if (flowType === "destination") {
				for (i = 0; i < n; i++) {
					var featureElement = this._dataArray[i];
					if (featureElement[this._destinationFeatureIdField] === featureId) {
						flowData.push({
							originFeatureId: featureElement[this._originFeatureIdField],
							originFeatureName: featureElement[this._originFeatureNameField] ? featureElement[this._originFeatureNameField] : featuresNames[featureElement[this._originFeatureIdField]],
							destinationFeatureId: featureElement[this._destinationFeatureIdField],
							destinationFeatureName: featureElement[this._destinationFeatureNameField] ? featureElement[this._destinationFeatureNameField] : featuresNames[featureElement[this._destinationFeatureIdField]],
							featureValue: parseFloat(featureElement[this._featureValueField])
						});

						//Update the value of the feature in reference layer....
						featureIds.push(featureElement[this._originFeatureIdField]);
						featureValues.push(parseFloat(featureElement[this._featureValueField]));

						originDestinationName = featureElement[this._destinationFeatureNameField] ? featureElement[this._destinationFeatureNameField] : featuresNames[featureElement[this._destinationFeatureIdField]];
					}
				}
			} else {
				console.error("FlowType not supported: " + flowType);
			}

			var flowKey = this.getFlowKey(featureId, flowType);
			var dataColumn = this.getDataColumn(flowKey, (flowType === "origin" ? (Localization.getValue(KEY_FLOW_ORIGIN) + " " + originDestinationName) : (Localization.getValue(KEY_FLOW_DESTINATION) + " " + originDestinationName)));
			if(this.styleTheme && this.styleTheme.colorTheme) this.styleTheme.colorTheme.dataColumn = dataColumn;
			if(this.styleTheme && this.styleTheme.sizeTheme) this.styleTheme.sizeTheme.dataColumn = dataColumn;
			if(this.styleTheme && this.styleTheme.symbologyTheme) this.styleTheme.symbologyTheme.dataColumn = dataColumn;

			if (this.currentActiveTheme !== this.styleTheme && this.styleTheme) {
				this._defaultThemeSet.removeTheme(this.currentActiveTheme);
				this.currentActiveTheme = this.styleTheme.clone();
				this._defaultThemeSet.addTheme(this.currentActiveTheme);
			}

			//Compute total value
			n = flowData.length;
			for (i = 0; i < n; i++) {
				totalValue += flowData[i].featureValue;
			}

			//Because we want percentage (taux) on the polygons and not the count:
			n = featureValues.length;
			for (i = 0; i < n; i++) {
				featureValues[i] = featureValues[i] / totalValue * 100;
			}

			//Update the value of the feature in reference layer....
			this.dataTableReferenceLayer.updateValues(dataColumn, featureIds, featureValues);

		}
		//console.log("featureID: " + featureId + " flowType: " + flowType + " totalValue: " + totalValue);
		this.totalValues[featureId + "." + flowType] = totalValue;

		//console.log(flowData);
		callbackFunction(flowData);
	};

	/**
	 * Create a data table on the reference layer to insert data about the flows in the map.
	 * 
	 * @method buildDataTable
	 */
	JSONFlowConnector.prototype.buildDataTable = function() {
		var interactiveLayer = this._referenceLayer;
		if (interactiveLayer === null) {
			throw new Error("Interactive layer is null");
		}

		this.dataTableReferenceLayer = interactiveLayer.getDataTable(REFERENCE_LAYER_FLOW_DATA_TABLE_ID);

		if (typeof this.dataTableReferenceLayer === 'undefined' || this.dataTableReferenceLayer === null) {
			this.dataTableReferenceLayer = new DataTableLocal(REFERENCE_LAYER_FLOW_DATA_TABLE_ID);
			this.dataTableReferenceLayer.name = Localization.getValue(KEY_REFERENCE_LAYER_FLOW_DATA_TABLE_NAME);
			interactiveLayer.dataElementRoot.addDataElement(this.dataTableReferenceLayer);
		}
	};

	/**
	 * Remove the data table from the reference layer and it's data columns.
	 * 
	 * @method cleanDataTable
	 */
	JSONFlowConnector.prototype.cleanDataTable = function() {
		if (typeof this.dataTableReferenceLayer !== 'undefined' && this.dataTableReferenceLayer !== null) {
			var dataColumns = this.dataTableReferenceLayer.getDataColumns();
			var colIds = [];
			for (var i = 0; i < dataColumns.length; i++) {
				var dataColumn = dataColumns[i];
				colIds.push(dataColumn.id);
			}
			this.dataTableReferenceLayer.removeDataColumns(colIds);
			this.dataTableReferenceLayer.removeAllDataRows();
		}
	};

	/**
	 * Get the data column with the specified id.
	 * If not found, it creates it and add it to the flow data table on the reference layer.
	 * 
	 * @method getDataColumn
	 * @param {Strin} dataColumnId Data column id we are looking for.
	 * @param {Strin} dataColumnName Data column name we are looking for.
	 * 
	 * @return {cartovista.viewer.data.DataColumn} The data column with de requested id.
	 */
	JSONFlowConnector.prototype.getDataColumn = function(dataColumnId, dataColumnName) {
		dataColumnName = dataColumnName || dataColumnId;
		var dataColumnsArray = [];
		var id = dataColumnId;
		var dataColumnMeasure = this.dataTableReferenceLayer.getDataColumns([dataColumnId])[0];
		if (!dataColumnMeasure) {
			dataColumnMeasure = new DataColumn(id, DataEnum.NUMBER);
			dataColumnMeasure.name = dataColumnName;
			dataColumnMeasure.decimalPlaces = 2;
			dataColumnMeasure.units = "%";
			dataColumnsArray.push(dataColumnMeasure);
			//this.dataColumnMeasure = dataColumnMeasure;
			this.dataTableReferenceLayer.addDataColumns(dataColumnsArray);
		}
		return dataColumnMeasure;
	};

	/**
	 * Abstract method that is implemented by subclasses.
	 * This method is called by the FlowController when a flow is removed from the map to unload the data from memory.
	 * 
	 * @method unloadFlowData
	 * @param {String} featureId Flow feature id to remove the data for.
	 * @param {String} flowType Flow type to remove the data for.
	 */
	JSONFlowConnector.prototype.unloadFlowData = function(featureId, flowType) {
		var flowKey = this.getFlowKey(featureId, flowType);
		var dataColumn = this.dataTableReferenceLayer.getDataColumns([flowKey])[0];
		if (dataColumn) {
			this.dataTableReferenceLayer.removeDataColumns([flowKey]);

			var dataColumns = this.dataTableReferenceLayer.getDataColumns();
			if (this.styleTheme && this.styleTheme.colorTheme && this.styleTheme.colorTheme.dataColumn === dataColumn) {
				if (dataColumns.length > 0) {
					this.styleTheme.colorTheme.dataColumn = dataColumns[0];
				} else {
					this.styleTheme.colorTheme.dataColumn = null;
				}
			}
			if (this.styleTheme && this.styleTheme.sizeTheme && this.styleTheme.sizeTheme.dataColumn === dataColumn) {
				if (dataColumns.length > 0) {
					this.styleTheme.sizeTheme.dataColumn = dataColumns[0];
				} else {
					this.styleTheme.sizeTheme.dataColumn = null;
				}
			}
			if (this.styleTheme && this.styleTheme.symbologyTheme && this.styleTheme.symbologyTheme.dataColumn === dataColumn) {
				if (dataColumns.length > 0) {
					this.styleTheme.symbologyTheme.dataColumn = dataColumns[0];
				} else {
					this.styleTheme.symbologyTheme.dataColumn = null;
				}
			}

			if (this.currentActiveTheme !== this.styleTheme && this.styleTheme) {
				this._defaultThemeSet.removeTheme(this.currentActiveTheme);
				if ((this.styleTheme.colorTheme && this.styleTheme.colorTheme.dataColumn) || (this.styleTheme.sizeTheme && this.styleTheme.sizeTheme.dataColumn) || (this.styleTheme.symbologyTheme && this.styleTheme.symbologyTheme.dataColumn)) {
					this.currentActiveTheme = this.styleTheme.clone();
					this._defaultThemeSet.addTheme(this.currentActiveTheme);
					if (this._map.activeThemeSet === this._defaultThemeSet) {
						this.currentActiveTheme.applyTheme();
					}
				} else {
					this.currentActiveTheme = null;
				}
			}

		}
	};

	/**
	 * Creates the default theme on the reference layer and adds it to the themeSet provided in argument.
	 * 
	 * @method createDefaultThemeSet
	 * @param {cartovista.viewer.thematics.ThemeSet} themeSet The theme set in which the default theme is added to.
	 * @param {cartovista.viewer.core.Map} map The map in which the theme set is added to.
	 */
	JSONFlowConnector.prototype.createDefaultThemeSet = function(themeSet) {
		this._defaultThemeSet = themeSet;


		//Create color theme for the reference layer
		var styleThemeInitial = themeSet.addThemeElement(this._referenceLayer, [null], ThemeElementEnum.THEME_BY_RANGES_OF_VALUES, RepresentationEnum.COLOR, false, this._map);
		this.styleTheme = styleThemeInitial.clone();
		this.styleTheme.colorTheme.palette = new PaletteFactory().getColorPalette(ColorPaletteEnum.BLUE_GREEN);
		this._defaultThemeSet.removeTheme(styleThemeInitial);
	};

	export default JSONFlowConnector;